import React, {memo, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Grid from '@mui/material/Grid';
import * as _ from 'lodash';
import moment from 'moment/moment';
import Button from '@mui/material/Button';
import clsx from 'clsx';
import {Typography} from '@mui/material';
import {useDemographicsStyles} from './admitStyles';
import {getEcdsVersion} from '../../store/reducers/dicts';
import {setSnackbar} from '../../store/reducers/globals';
import AgyleTextField from '../../components/ui/fields/agyleTextField';
import AgyleButton from '../../components/ui/buttons/agyleButton';
import AgyleDictionaryField from '../../components/ui/selector/agyleDictionaryField';
import AgyleDialog from '../../components/modal/agyleDialog';
import InformationBox from '../../components/other/informationBoxNew';
import AgyleDatePicker from '../../components/ui/fields/agyleDatePicker';
import AgyleLoadingSpinner from '../../components/other/agyleLoadingSpinner';

const initialUnknownPatientState = () => ({
  firstName: "",
  lastName: "",
  dob: null,
  yearOfBirth: "",
  speedRegistry: false,
});

const mnemonicValues = [
  "Alpha",
  "Bravo",
  "Delta",
  "Foxtrot",
  "Golf",
  "Hotel",
  "Kilo",
  "Lima",
  "Papa",
  "Quebec",
  "Sierra",
  "Tango",
  "Uniform",
  "Whiskey",
  "Yankee",
  "Zulu",
];

const UnknownPatientBanner = (props) => {
  const { t } = useTranslation();
  const classes = useDemographicsStyles();
  const dispatch = useDispatch();
  const version = dispatch(getEcdsVersion(null));

  const [isLoading, setIsLoading] = useState(false);
  const [unknownPatientState, setUnknownPatientState] = useState(() =>
    initialUnknownPatientState(),
  );

  const {
    genderOptions,
    setAdmitToUnknown,
    setActiveStep,
    onClose,
    addAdmit,
    admit,
    // setAdmit,
    admission,
    setAdmission,
    fastReg,
    speedRegFlag,
    arrivalModeOptions,
    isArrivalModeOptionsLoading,
    chiefComplaintOptions,
    isChiefComplaintOptionsLoading,
    conveyingAmbulanceOptions,
    conveyingAmbulanceLoading,
  } = props;

  useEffect(() => {
    let patientObj;
    if (
      fastReg &&
      admit.firstName &&
      admit.familyName &&
      admit.dob &&
      admit.gender &&
      admit.hospitalNumber
    ) {
      patientObj = {
        firstName: admit.firstName,
        lastName: admit.familyName,
        dob: admit.dob,
        hospitalNumber: admit.hospitalNumber,
        gender:
          admit.gender.__description === "Male" ||
          admit.gender.__description === "Female"
            ? admit.gender
            : null,
      };
      setUnknownPatientState((prevState) => ({
        ...prevState,
        ...patientObj,
      }));
    }
  }, []);

  const handleUnknownPatientChange = (e) => {
    const { name, value } = e.target;

    setUnknownPatientState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const isPatientArrivedByAmbulance = useMemo(
    () =>
      admission?.modeOfArrival?.group === "ambulance" ||
      admission?.modeOfArrival?.ambulance,
    [admission?.modeOfArrival],
  );
  //
  // const handleToggleSpeedRegistry = (e) => {
  //   let admitObj = {
  //     ...admit,
  //     speedRegistry: e,
  //   };
  //
  //   setAdmit(admitObj);
  // };

  const handleRandomizeField = (field) => {
    const randomIndex = Math.floor(Math.random() * (mnemonicValues.length - 1));
    const randomValue = mnemonicValues[randomIndex];

    setUnknownPatientState((prevState) => ({
      ...prevState,
      [field]: randomValue,
    }));
  };

  const checkIfUnknownValid = () => {
    const { firstName, lastName, gender } = unknownPatientState;
    let formIsValid = true;

    if (
      !gender ||
      (gender.__description !== "Male" && gender.__description !== "Female")
    ) {
      formIsValid = false;
    }

    if (fastReg || speedRegFlag) {
      if (!admission.chiefComplaint || !admission.modeOfArrival)
        formIsValid = false;
    }

    return !(!firstName || !lastName || !formIsValid);
  };

  const checkIfDateIsValid = () => {
    const unknownPatientYear = unknownPatientState.yearOfBirth;

    if (unknownPatientState.dob && unknownPatientState.dob.isValid()) {
      return true;
    }

    if (!unknownPatientYear || isNaN(unknownPatientYear)) {
      return false;
    }

    return !(
      +unknownPatientYear < 1890 ||
      +unknownPatientYear > new Date().getFullYear()
    );
  };

  const handleAddAdmit = async (data) => {
    setIsLoading(true);

    try {
      await addAdmit(data);
    } catch (err) {
      dispatch(setSnackbar({ type: 'error', message: 'Patient has not been created. Please try again later'}))
    } finally {
      setIsLoading(false)
    }
  }

  const confirmSetUnknown = async () => {
    let patientObj = {
      ...unknownPatientState,
      speedRegistry: admit.speedRegistry || speedRegFlag,
      hospitalNumber: admit.hospitalNumber,
    };

    if (!checkIfDateIsValid()) {
      dispatch(
        setSnackbar({
          open: true,
          message: `Please enter a valid year of birth.`,
          type: "error",
        }),
      );
      return;
    }
    if (!checkIfUnknownValid()) {
      dispatch(
        setSnackbar({
          open: true,
          message: `Please fill in all required fields.`,
          type: "error",
        }),
      );
      return;
    }

    setAdmitToUnknown(patientObj);

    if (speedRegFlag || fastReg || admit.speedRegistry) {
        const objToAdmit = setAdmitToUnknown(patientObj, true);

        handleAddAdmit({ ...objToAdmit, ...admission });
      } else {
        onClose();
        setActiveStep(2);
      }
  };

  const renderRandomSection = (field, label, hideButton) => {
    return (
      <>
        <Grid
          item
          flexGrow={1}
          xs={field === "lastName" && hideButton ? 6 : ""}
        >
          <AgyleTextField
            onChange={handleUnknownPatientChange}
            name={field}
            value={unknownPatientState[field]}
            label={label}
            error={!unknownPatientState[field]}
            fullWidth
            noBorder={false}
          >
            {t(label)}
          </AgyleTextField>
        </Grid>

        {!hideButton && (
          <Grid item>
            <AgyleButton
              disabled={fastReg}
              onClick={() => handleRandomizeField(field)}
            >
              Randomize {label}
            </AgyleButton>
          </Grid>
        )}
      </>
    );
  };

  const renderDictionaryFields = (field, label, dict, dictLoading) => {
    const ccDescription =
      version === "V3" ? "parentTerm" : "snomedPreferredTerm";
    const handleDictChange = (_e, val) => {
      let admissionToSend = {
        ...admission,
        [field]: val,
      };

      setAdmission(admissionToSend);
    };

    // TO BE REMOVED LATER
    const dictOptions =
      field === "chiefComplaint"
        ? !_.isEmpty(dict) &&
          dict.filter(
            (item) =>
              item?.[ccDescription]?.toLowerCase() !==
              "not applicable to max terms",
          )
        : dict;

    const localOptions = (prop, option, labelText, d) => {
      return (
        <li {...prop}>
          <span className={classes.ward}>
            {`${field === "chiefComplaint" ? option[ccDescription] : option?.__description}`}
          </span>
        </li>
      );
    };

    return (
      <>
        <Grid item flexGrow={1} xs={field === "modeOfArrival" ? 6 : ""}>
          <AgyleDictionaryField
            onChange={handleDictChange}
            options={dictOptions}
            name={field}
            loading={dictLoading}
            value={admission[field]}
            getOptionLabel={(option) =>
              field === "chiefComplaint"
                ? option[ccDescription]
                : option.__description
            }
            label={label}
            noBorder={false}
            error={!admission[field]}
            renderLocalOption={localOptions}
            groupBy={(option) =>
              (field === "chiefComplaint" || version === "V4") && option.group
            }
          >
            {t(label)}
          </AgyleDictionaryField>
        </Grid>
      </>
    );
  };

  useEffect(() => {
    const defaultGender =
      genderOptions &&
      genderOptions.find((item) => item?.__description === "Not Known");

    if (defaultGender) {
      setUnknownPatientState((prevState) => ({
        ...prevState,
        gender: defaultGender,
      }));
    }

    if (speedRegFlag || fastReg) {
      setAdmission((prevState) => ({
        ...prevState,
        admitDate: moment(),
      }));
    }
  }, [genderOptions?.length, speedRegFlag, fastReg]);

  const getConveyingAmbulanceOptions = () => {
    if (admission.conveyingAmbulanceOrgId) {
      return (
        _.find(conveyingAmbulanceOptions, {
          __description: admission.conveyingAmbulanceOrgId,
        }) || null
      );
    }
    return null;
  };

  const submitButton = () => {
    return !fastReg && !speedRegFlag ? (
      <Button
        color="tertiary"
        disabled={!checkIfUnknownValid()}
        onClick={confirmSetUnknown}
      >
        {t("Confirm")}
      </Button>
    ) : (
      <AgyleButton
        onClick={() => confirmSetUnknown()}
        variant="contained"
        disabled={!checkIfUnknownValid() || isLoading}
      >
        {t("Create New Attendance")}
      </AgyleButton>
    );
  };

  return (
    <AgyleDialog
      title={t("New Temporary Patient")}
      onClose={() => {
        if (speedRegFlag || fastReg) {
          setActiveStep(0);
        }
        onClose();
      }}
      fullWidth
      open
      darkContentBackground
      closeButtonOnHeader
      disableActionsCloseButton
      disableSave
      buttons={[submitButton()]}
    >
      {isLoading ? <AgyleLoadingSpinner label={"Creating New Attendance"} /> : (
          <>
            {!fastReg && (
                <InformationBox type={"warning"} sx={{ mb: 2 }}>
                  <div className={clsx(classes.currentPatient)}>
                    {speedRegFlag ? (
                        <Typography variant="subtitle2" component="div">
                          {t("Temporary Patient Attendance")}
                        </Typography>
                    ) : (
                        <>
                          <Typography variant="button" component="div">
                            {t("Patient is unknown.")}
                          </Typography>
                          <Typography variant="body2" component="div">
                            {t(
                                "Please confirm you want to set patient as unknown and fill required fields.",
                            )}
                          </Typography>
                        </>
                    )}
                  </div>
                </InformationBox>
            )}

            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                style={{ paddingBottom: "20px" }}
            >
              {renderRandomSection(
                  "firstName",
                  "First Name",
                  fastReg || speedRegFlag,
              )}
              {renderRandomSection("lastName", "Last Name", fastReg || speedRegFlag)}
              {!fastReg && !speedRegFlag ? (
                  <Grid
                      item
                      xs={6}
                      className={clsx(!fastReg && classes.unknownPatientDate)}
                  >
                    <Typography
                        variant="body1"
                        component="div"
                        sx={{ pt: 1.2, mr: 1, fontWeight: "bold" }}
                        textAlign="center"
                    >
                      01/01
                    </Typography>

                    <AgyleTextField
                        onChange={(e) =>
                            setUnknownPatientState((prevState) => ({
                              ...prevState,
                              yearOfBirth: e.target.value,
                            }))
                        }
                        type="number"
                        noBorder={false}
                        value={unknownPatientState["yearOfBirth"]}
                        label="Year of Birth"
                        error={!unknownPatientState.yearOfBirth}
                        fullWidth
                    />
                  </Grid>
              ) : (
                  <Grid item flexGrow={1}>
                    <AgyleDatePicker
                        onChange={(e) =>
                            setUnknownPatientState((prevState) => ({
                              ...prevState,
                              dob: e,
                            }))
                        }
                        type="date"
                        label={t("Date of birth")}
                        maxDate={moment()}
                        noBorder={false}
                        value={unknownPatientState.dob}
                        error={!unknownPatientState.dob}
                    />
                  </Grid>
              )}

              <Grid item xs={6}>
                <AgyleDictionaryField
                    onChange={(_e, val) =>
                        setUnknownPatientState((prevState) => ({
                          ...prevState,
                          gender: val,
                        }))
                    }
                    // disabled={fastReg && (
                    //     unknownPatientState.gender || (
                    //         unknownPatientState?.gender?.__description === "Male" && unknownPatientState?.gender?.__description === "Female"
                    //     )
                    // )}
                    error={!unknownPatientState.gender}
                    value={unknownPatientState.gender}
                    options={genderOptions}
                    label="Gender"
                    noBorder={false}
                />
              </Grid>

              {(speedRegFlag || fastReg) && (
                  <>
                    {renderDictionaryFields(
                        "chiefComplaint",
                        "Chief Complaint",
                        chiefComplaintOptions,
                        isChiefComplaintOptionsLoading,
                    )}
                    {renderDictionaryFields(
                        "modeOfArrival",
                        "Arrival Mode",
                        arrivalModeOptions,
                        isArrivalModeOptionsLoading,
                    )}
                    {
                      <Grid item flexGrow={1}>
                        <AgyleDatePicker
                            onChange={(e) =>
                                setAdmission((prevState) => ({
                                  ...prevState,
                                  admitDate: e,
                                }))
                            }
                            label={t("Attendance Date")}
                            type={"dateTime"}
                            noBorder={false}
                            value={admission.admitDate}
                            error={!admission.admitDate}
                        />
                      </Grid>
                    }
                    {isPatientArrivedByAmbulance && (
                        <>
                          <Grid item xs={12}>
                            <AgyleTextField
                                type="ambulance"
                                required
                                multiline
                                fullWidth
                                noBorder={false}
                                variant="outlined"
                                label={t("Ambulance Incident Number")}
                                error={
                                    !admission.ambulanceIncidentNumber &&
                                    !(fastReg || speedRegFlag)
                                }
                                value={admission.ambulanceIncidentNumber || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const valCare = val ? `${val.replace("-", "")}-` : "";

                                  setAdmission((prevState) => ({
                                    ...prevState,
                                    ambulanceIncidentNumber: val,
                                    careContactIdentifier: valCare,
                                  }));
                                }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <AgyleTextField
                                required
                                multiline
                                fullWidth
                                noBorder={false}
                                variant="outlined"
                                label={t("Care Contact Identifier")}
                                error={
                                    !admission.careContactIdentifier &&
                                    !(fastReg || speedRegFlag)
                                }
                                value={admission.careContactIdentifier || ""}
                                onChange={(e) =>
                                    setAdmission((prevState) => ({
                                      ...prevState,
                                      careContactIdentifier: e.target.value,
                                    }))
                                }
                                type="ambulance"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <AgyleDictionaryField
                                label={`Org Id - Conveying Ambulance Trust`}
                                onChange={(_e, val) =>
                                    setAdmission((prevState) => ({
                                      ...prevState,
                                      conveyingAmbulanceOrgId: val?.__description,
                                    }))
                                }
                                options={conveyingAmbulanceOptions}
                                error={
                                    !admission.conveyingAmbulanceOrgId &&
                                    !(fastReg || speedRegFlag)
                                }
                                value={getConveyingAmbulanceOptions()}
                                required
                                noBorder={false}
                                loading={conveyingAmbulanceLoading}
                            />
                          </Grid>
                        </>
                    )}

                    <Grid item xs={6}>
                      <AgyleTextField
                          required
                          multiline
                          fullWidth
                          noBorder={false}
                          variant="outlined"
                          label={t("History of Presenting Complaint")}
                          value={admission?.historyOfPresentingComplaint}
                          onChange={(e) =>
                              setAdmission((prevState) => ({
                                ...prevState,
                                historyOfPresentingComplaint: e.target.value,
                              }))
                          }
                      />
                    </Grid>
                  </>
              )}
            </Grid>
          </>
      )}
    </AgyleDialog>
  );
};

export default memo(UnknownPatientBanner, _.isEqual);
